import { useKeycloak } from '@react-keycloak/web';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  NETWORK_ERROR_SNACKBAR_KEY,
  getBackendErrorMessage,
  getErrorKind,
} from '@/shared/lib/errors';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

let isRedirectingToLogin = false;

const redirectToLogin = (keycloak: ReturnType<typeof useKeycloak>['keycloak']) => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  keycloak.createLoginUrl({ redirectUri: window.location.href }).then((url: string) => {
    window.location.replace(url);
  });
};

const useQueryClientSetup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();

  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;
  const keycloakRef = useRef(keycloak);
  keycloakRef.current = keycloak;
  const tRef = useRef(t);
  tRef.current = t;

  const [queryClient] = useState(() => {
    // Named so mutationCache.onError below can tell a mutation-specific onError
    // (which already shows the real backend message) apart from this default.
    const defaultMutationOnError = (err: unknown) => {
      const axiosError = err as AxiosError;
      const status = axiosError.response?.status;
      if (status === 401 || status === 403) return;
      if (getErrorKind(err) === 'network') {
        enqueueNetworkErrorSnackbar();
        return;
      }
      enqueueSnackbarRef.current(getBackendErrorMessage(axiosError), {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });
    };

    // axios reports an unreachable server as a bare "Network Error"; say it in the
    // user's language, once, however many requests failed at the same time.
    const enqueueNetworkErrorSnackbar = () => {
      enqueueSnackbarRef.current(tRef.current('errors.network'), {
        variant: 'error',
        key: NETWORK_ERROR_SNACKBAR_KEY,
        preventDuplicate: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      });
    };

    return new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 0,
          // Inactive queries are GC'd 5 min after their last observer unmounts
          // (React Query's default). The previous 24h meant the cache effectively
          // never released queries within a session — on long-lived, query-heavy
          // tabs that accumulated into an OOM. Cross-session restore is handled
          // separately by the localStorage persister (maxAge), not cacheTime.
          cacheTime: 1000 * 60 * 5,
          refetchOnMount: true,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          retry: 1,
          retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
        },
        mutations: {
          retry: 0,
          onError: defaultMutationOnError,
        },
      },
      queryCache: new QueryCache({
        onError: (err: unknown, query) => {
          const axiosError = err as AxiosError;
          if (axiosError.response?.status === 401) {
            redirectToLogin(keycloakRef.current);
          } else if (axiosError.response?.status === 403 && !query.options?.onError) {
            // No query-specific handler is going to show a more precise message — fall back to the generic toast.
            enqueueSnackbarRef.current(tRef.current('errors.action_forbidden'), {
              variant: 'error',
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            });
          } else if (getErrorKind(err) === 'network' && !query.options?.onError) {
            enqueueNetworkErrorSnackbar();
          }
        },
      }),
      mutationCache: new MutationCache({
        onError: (err: unknown, _variables, _context, mutation) => {
          const axiosError = err as AxiosError;
          if (axiosError.response?.status === 401) {
            redirectToLogin(keycloakRef.current);
          } else if (
            axiosError.response?.status === 403 &&
            mutation.options.onError === defaultMutationOnError
          ) {
            // A mutation-specific onError already shows the real backend message — don't override it here.
            enqueueSnackbarRef.current(tRef.current('errors.action_forbidden'), {
              variant: 'error',
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            });
          }
        },
      }),
    });
  });

  return queryClient;
};

export default useQueryClientSetup;
